/* eslint-disable no-case-declarations */
// wrapped with modal
// comes selecting activity from the template
// 3 types od assignment based on th dropdown selection
// 1. custom task
// 2. one time task
// 3. repeating task
// this component is used for creating new activity and editing existing activity
import React, { ChangeEvent, useEffect, useState } from 'react';
import { Box, MenuItem, Select, SelectChangeEvent, Typography, styled, Grid } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import CustomTask from './CustomTask';
import OneTimeTask from './OneTimeTask';
import RepeatTask from './RepeatTask';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  AssetType,
  IActivity,
  IStepMediaAsset,
  addActivityRequest,
  closeModalOnSuccess,
  updateActivityRequest,
} from '../../redux/activity/activitySlice';
import { ActivityTypeEnum, activityTypeOptions } from '../../types/activity';
import StyledBackButton from '../Common/StyledBackButton';
import StyledButton from '../Common/StyledButton';
import { slimScrollbarGreenStyles } from '../../utils/styles';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

interface IAssignActivityContainerProps {
  handleBack: () => void;
  closeModel: () => void;
  isEditMode?: boolean;
  existingActivityData?: IActivity | null;
}

// can be {custom: customMessage},{date: date},{time: time}
interface IActivityScheduleUpdateData {
  [key: string]: string;
}
const StyledBox = styled(Box)(() => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',

  paddingBottom: '36px',
  gap: '11px',
  background:
    'linear-gradient(0deg, rgba(124, 174, 190, 0.20) 0%, rgba(124, 174, 190, 0.20) 100%), #FFF',
  borderRadius: '10px 10px 0 0',
  // '&::after': {
  //   content: '""',
  //   position: 'absolute',
  //   bottom: '-20px',
  //   left: '50%',
  //   transform: 'translateX(-50%)',
  //   borderTop: '20px solid',
  //   borderColor: theme.palette.secondary.main,
  //   borderLeft: '20px solid transparent',
  //   borderRight: '20px solid transparent',
  // },
}));

const StyledSelect = styled(Select)(({ theme, error }) => ({
  height: '48px',
  borderRadius: '10px',
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&&&:before': {
      borderBottom: 'none',
    },
    '&&:after': {
      borderBottom: 'none',
    },
  },
  '& .MuiSelect-select': {
    borderRadius: '5px',
    display: 'flex',
    alignItems: 'center',
    padding: '10px',
    '&:focus': {
      borderRadius: '5px',
    },
    '&::placeholder': {
      opacity: 0.2,
    },
  },
  '& fieldset': {
    padding: '12px',
    color: theme.palette.primary.main,
    border: error
      ? '1px solid var(--Alert-Sunset-Orange, #FF9A9A) !important'
      : '1px solid var(--Accent-Moonstone-Blue, #7CAEBE) !important',
  },
  '&:hover fieldset': {
    border: error
      ? '1px solid var(--Alert-Sunset-Orange, #FF9A9A) !important'
      : '1px solid var(--Accent-Moonstone-Blue, #7CAEBE) !important',
  },
  '&.Mui-focused fieldset': {
    border: '1px solid var(--Accent-Moonstone-Blue, #7CAEBE) !important',
  },
}));
interface IActivityData {
  custom: {
    customMessage: string;
  };
  oneTime: {
    startDate: string;
  };
  repeat: {
    days: number[];
  };
}

function AssignActivityContainer({
  handleBack,
  closeModel,
  isEditMode = false,
  existingActivityData = null,
}: IAssignActivityContainerProps) {
  const [activityType, setActivityType] = useState('');
  const draftedActivity = useAppSelector((state) => state.activity.draftActivity);
  const coach = useAppSelector((state) => state.coach.coach);
  const coachId = coach?.id || null;
  const dispatch = useAppDispatch();
  // client id from url
  const { id } = useParams();
  const [activityData, setActivityData] = useState<IActivityData>({
    custom: {
      customMessage: '',
    },
    oneTime: {
      startDate: new Date().toString().split('T')[0],
    },
    repeat: {
      days: [0, 0, 0, 0, 0, 0, 0],
    },
  });
  const { t } = useTranslation();
  const { updateActivityLoading, addActivityLoading, isAddUpdateActivitySuccess } = useAppSelector(
    (state) => state.activity
  );

  useEffect(() => {
    if (isEditMode && existingActivityData) {
      setActivityType(existingActivityData.schedule || '');
      setActivityData({
        custom: {
          customMessage: existingActivityData.customMessage || '',
        },
        oneTime: {
          // have to get start date from existing activity
          startDate: dayjs(existingActivityData.startAt).format('YYYY-MM-DD'),
        },
        repeat: {
          days: existingActivityData.repeatOn || [0, 0, 0, 0, 0, 0, 0],
        },
      });
    }
  }, [isEditMode, existingActivityData]);

  useEffect(() => {
    if (!isEditMode && draftedActivity) {
      setActivityType(draftedActivity.frequency || '');
      setActivityData({
        custom: {
          customMessage: draftedActivity.situation || '',
        },
        oneTime: {
          // have to get start date from existing activity
          startDate: new Date().toISOString().split('T')[0],
        },
        repeat: {
          days: [1, 1, 1, 1, 1, 1, 1],
        },
      });
    }
  }, [isEditMode, draftedActivity]);

  const updateActivityData = (type: keyof IActivityData, data: IActivityScheduleUpdateData) => {
    setActivityData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        ...data,
      },
    }));
  };

  const handleCustomChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    updateActivityData('custom', { [name]: value });
  };

  const handleOneTimeChange = (field: string, value: string) => {
    updateActivityData('oneTime', { [field]: value });
  };

  const toggleRepeatDay = (dayIndex: number) => {
    setActivityData((prev) => {
      const newDays = [...prev.repeat.days];
      newDays[dayIndex] = newDays[dayIndex] === 0 ? 1 : 0;
      return {
        ...prev,
        repeat: {
          days: newDays,
        },
      };
    });
  };
  const isValidActivityData = () => {
    // const today = dayjs().startOf('day');

    switch (activityType) {
      case ActivityTypeEnum.SITUATIONAL:
        return activityData.custom.customMessage.trim() !== '';
      case ActivityTypeEnum.ONETIME:
        const startDate = dayjs(activityData.oneTime.startDate);
        const today = dayjs();
        const yesterday = today.subtract(1, 'day');
        if (startDate.isBefore(yesterday)) {
          return false;
        }
        return startDate.isValid();
      case ActivityTypeEnum.RECCURENT:
        return activityData.repeat.days.some((day) => day === 1);
      default:
        return false;
    }
  };

  const renderActivityOptions = (activity: string) => {
    switch (activity) {
      case ActivityTypeEnum.SITUATIONAL:
        return (
          <CustomTask customData={activityData.custom} handleCustomChange={handleCustomChange} />
        );
      case ActivityTypeEnum.ONETIME:
        return (
          <OneTimeTask
            oneTimeData={activityData.oneTime}
            handleOneTimeChange={handleOneTimeChange}
          />
        );
      case ActivityTypeEnum.RECCURENT:
        return <RepeatTask repeatData={activityData.repeat} handleRepeatChange={toggleRepeatDay} />;
      default:
        return null;
    }
  };

  const handleAssignActivity = () => {
    if (!id || !coachId) return;
    if (!isEditMode && draftedActivity) {
      const activityInput = {
        clientId: id,
        coachId,
        name: draftedActivity.name,
        objective: draftedActivity.objective,
        schedule: activityType as ActivityTypeEnum,
        repeatOn: activityData.repeat.days,
        customMessage:
          activityType === ActivityTypeEnum.SITUATIONAL ? activityData.custom.customMessage : '',
        startAt: activityData.oneTime.startDate ? activityData.oneTime.startDate : new Date(),
        isCompleted: false,
        isAddToLibrary: draftedActivity.isAddToLibrary,
        steps: draftedActivity.ActivityStep?.map((step) => ({
          text: step.text,
          description: step.description,
          stepOrder: step.stepOrder,
          asset: step.asset,
          assetType: step.assetType,
          contentType: step.contentType,
          isAdvanceStep: step.isAdvanceStep,
          advanceStepType: step.advanceStepType,
          advanceStepData: step.advanceStepData,
        })),
      };
      let stepMediaAssets: IStepMediaAsset[] = [];
      if (draftedActivity.ActivityStep) {
        stepMediaAssets = draftedActivity.ActivityStep.map((step) => {
          if (step.assetType !== AssetType.TEXT) {
            return {
              assetFile: step.assetFile as File,
              assetType: step.asset,
            };
          }
          return null;
        }).filter((step) => step !== null) as IStepMediaAsset[];
      }
      dispatch(addActivityRequest({ activityInput, stepMediaAssets, coachId }));
    }
    if (isEditMode && existingActivityData?.id) {
      const editedActivityInput = {
        id: existingActivityData.id,
        clientId: id,
        coachId,
        schedule: activityType as ActivityTypeEnum,
        customMessage: activityData.custom.customMessage,
        isActive: existingActivityData.isActive,
        startAt: activityData.oneTime.startDate ? activityData.oneTime.startDate : new Date(),
        repeatOn: activityData.repeat.days,
      };
      dispatch(updateActivityRequest({ data: editedActivityInput, statusUpdate: false }));
    }
  };
  const handleAssignActivityType = (event: SelectChangeEvent<unknown>) => {
    setActivityType(event.target.value as string);
  };
  useEffect(() => {
    isValidActivityData();
  }, [activityData]);

  useEffect(() => {
    if (isAddUpdateActivitySuccess) {
      closeModel();
      dispatch(closeModalOnSuccess());
    }
  }, [isAddUpdateActivitySuccess]);
  return (
    <Grid item xs={12} display="flex" flexDirection="column">
      <StyledBox>
        <Box p={1} width="100%" textAlign="end">
          <img
            src="/assets/icons/close-circle-large.svg"
            alt="close-icon"
            onClick={closeModel}
            role="presentation"
            style={{
              cursor: 'pointer',
              maxHeight: '22px',
            }}
          />
        </Box>
        <img src="/assets/icons/user-check.svg" alt="assign-activity" />
        <Typography variant="title2Bold" textAlign="center" color="primary.main">
          {t('activity.assignActivity.assignActivity')}
        </Typography>
      </StyledBox>
      <Box
        bgcolor="background.paper"
        p="52px 32px 36px 32px"
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        borderRadius="0 0 10px 10px"
        maxHeight={500}
        sx={{ overflowY: 'auto', ...slimScrollbarGreenStyles }}
      >
        <Box mb="48px">
          <Typography
            variant="h4"
            textAlign="center"
            color="primary.main"
            textTransform="capitalize"
          >
            {!isEditMode ? draftedActivity?.name : existingActivityData?.name}
          </Typography>
        </Box>

        <Box>
          {/* label with dropdown here */}
          <Typography variant="meta" color="primary.main">
            {t('activity.assignActivity.whenToCompleteActivity')}
          </Typography>
          <StyledSelect
            id="activity-select"
            value={activityType}
            onChange={handleAssignActivityType}
            variant="outlined"
            fullWidth
            displayEmpty
          >
            <MenuItem value="" disabled>
              <Typography variant="bodyRegular" color="primary.main">
                {t('activity.assignActivity.pleaseSelect')}
              </Typography>
            </MenuItem>
            {activityTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Typography variant="bodyRegular" color="primary.main">
                  {t(option.label)}
                </Typography>
              </MenuItem>
            ))}
          </StyledSelect>
        </Box>

        {
          // render activity based on the selection
          renderActivityOptions(activityType)
        }
        <Box display="flex" justifyContent="space-between" gap="20px" mt="48px">
          {!isEditMode && (
            <StyledBackButton
              variant="outlined"
              fullWidth
              onClick={handleBack}
              disabled={updateActivityLoading || addActivityLoading}
            >
              <span>{t('common.back')}</span>
            </StyledBackButton>
          )}
          <StyledButton
            fullWidth
            onClick={handleAssignActivity}
            disabled={
              !activityType ||
              !isValidActivityData() ||
              (activityType === ActivityTypeEnum.SITUATIONAL &&
                activityData.custom.customMessage.trim().length > 30)
            }
            loading={updateActivityLoading || addActivityLoading}
            loadingText={isEditMode ? t('common.updating') : t('common.assigning')}
          >
            {isEditMode ? t('common.update') : t('common.assign')}
          </StyledButton>
        </Box>
      </Box>
    </Grid>
  );
}

AssignActivityContainer.defaultProps = {
  isEditMode: false,
  existingActivityData: {},
};

export default AssignActivityContainer;
